import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Sun, CloudSun, Cloud, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { listBeds, createBed, updateBed, deleteBed, getBedLayout, BED_TYPES, SUN, formatApiError } from "@/lib/api";
import { BedGridEditor } from "@/components/bed-grid/BedGridEditor";
import { useGardens } from "@/contexts/GardenContext";
import UpgradeBanner, { upgradeMessageFrom } from "@/components/UpgradeBanner";

const SunIcon = ({ value }) => {
    if (value === "full sun") return <Sun className="w-4 h-4 text-[var(--vm-tertiary)]" strokeWidth={1.5} />;
    if (value === "partial shade") return <CloudSun className="w-4 h-4 text-[var(--vm-sage)]" strokeWidth={1.5} />;
    return <Cloud className="w-4 h-4 text-[var(--vm-text-muted)]" strokeWidth={1.5} />;
};

const blank = {
    name: "",
    type: "raised bed",
    length_cm: 240,
    width_cm: 120,
    sun_exposure: "full sun",
    soil_notes: "",
    grid_rows: 0,
    grid_cols: 0,
};

export default function Beds() {
    const { activeGardenId, activeGarden } = useGardens();
    const [beds, setBeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(blank);
    const [layouts, setLayouts] = useState({});
    const [upgradeMsg, setUpgradeMsg] = useState(null);

    const load = async () => {
        if (!activeGardenId) {
            setBeds([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const bs = await listBeds(activeGardenId);
            setBeds(bs);
            const layoutEntries = await Promise.all(
                bs.map(b => getBedLayout(b.id).then(l => [b.id, l]).catch(() => [b.id, null]))
            );
            setLayouts(Object.fromEntries(layoutEntries));
        } catch (e) {
            // After client-side retries (lib/api.js) — fall through to
            // an empty list rather than toasting. The page still renders
            // and the user can click "New bed" or refresh.
            // eslint-disable-next-line no-console
            console.warn("beds load failed:", e?.message);
            setBeds([]);
            setLayouts({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeGardenId]);

    const startCreate = () => { setEditingId(null); setForm(blank); setOpen(true); };
    const startEdit = (bed) => {
        setEditingId(bed.id);
        setForm({
            name: bed.name,
            type: bed.type,
            length_cm: bed.length_cm,
            width_cm: bed.width_cm,
            sun_exposure: bed.sun_exposure,
            soil_notes: bed.soil_notes || "",
            grid_rows: bed.grid_rows ?? 0,
            grid_cols: bed.grid_cols ?? 0,
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.name.trim()) {
            toast.error("Bed needs a name");
            return;
        }
        if (!activeGarden) {
            toast.error("Select a garden first");
            return;
        }
        try {
            if (editingId) {
                await updateBed(editingId, form);
                toast.success("Bed updated");
            } else {
                await createBed({ ...form, garden_id: activeGarden.id });
                toast.success("Bed added");
            }
            setOpen(false);
            setUpgradeMsg(null);
            await load();
        } catch (e) {
            const msg = upgradeMessageFrom(e);
            if (msg) {
                setOpen(false);
                setUpgradeMsg(msg);
            } else {
                toast.error(formatApiError(e));
            }
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this bed and all its crops?")) return;
        try {
            await deleteBed(id);
            toast.success("Bed deleted");
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    };

    return (
        <div className="space-y-10" data-testid="beds-page">
            {upgradeMsg && <UpgradeBanner message={upgradeMsg} />}
            <PageHeader
                overline="Bed Plans"
                title="Garden Beds"
                description="Each bed is a small ecosystem. Define dimensions, sun and soil — then plan crops within."
                action={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={startCreate} data-testid="add-bed-btn" className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6">
                                <Plus className="w-4 h-4 mr-2" /> Add bed
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[var(--vm-card)] border-[var(--vm-border)]">
                            <DialogHeader>
                                <DialogTitle className="font-serif-display text-2xl">{editingId ? "Edit bed" : "New bed"}</DialogTitle>
                            </DialogHeader>
                            <div className="grid gap-4 py-2">
                                <div className="grid gap-2">
                                    <Label>Name</Label>
                                    <Input data-testid="bed-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bed A — South Border" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="grid gap-2">
                                        <Label>Type</Label>
                                        <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                                            <SelectTrigger data-testid="bed-type-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {BED_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Sun</Label>
                                        <Select value={form.sun_exposure} onValueChange={v => setForm({ ...form, sun_exposure: v })}>
                                            <SelectTrigger data-testid="bed-sun-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {SUN.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="grid gap-2">
                                        <Label>Length (cm)</Label>
                                        <Input type="number" data-testid="bed-length-input" value={form.length_cm} onChange={e => setForm({ ...form, length_cm: Number(e.target.value) })} />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Width (cm)</Label>
                                        <Input type="number" data-testid="bed-width-input" value={form.width_cm} onChange={e => setForm({ ...form, width_cm: Number(e.target.value) })} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="grid gap-2">
                                        <Label>Grid rows</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            data-testid="bed-grid-rows-input"
                                            value={form.grid_rows}
                                            onChange={e => setForm({ ...form, grid_rows: Number(e.target.value) })}
                                        />
                                        <span className="text-[10px] text-[var(--vm-text-muted)]">0 = auto-derive (~30 cm/cell)</span>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Grid cols</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            data-testid="bed-grid-cols-input"
                                            value={form.grid_cols}
                                            onChange={e => setForm({ ...form, grid_cols: Number(e.target.value) })}
                                        />
                                        <span className="text-[10px] text-[var(--vm-text-muted)]">0 = auto-derive (~30 cm/cell)</span>
                                    </div>
                                </div>
                                <div className="grid gap-2">
                                    <Label>Soil notes</Label>
                                    <Textarea data-testid="bed-soil-input" value={form.soil_notes} onChange={e => setForm({ ...form, soil_notes: e.target.value })} placeholder="e.g. heavy clay, amended with compost" />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setOpen(false)} data-testid="bed-cancel-btn">Cancel</Button>
                                <Button onClick={submit} data-testid="bed-save-btn" className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                                    {editingId ? "Save changes" : "Create bed"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            {loading ? (
                <div className="text-[var(--vm-text-muted)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading beds…</div>
            ) : beds.length === 0 ? (
                <div className="journal-card p-12 text-center">
                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">No beds yet</h3>
                    <p className="text-[var(--vm-text-secondary)] mb-5">Start by adding the dimensions of your first raised bed or polytunnel.</p>
                    <Button onClick={startCreate} data-testid="empty-add-bed-btn" className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                        <Plus className="w-4 h-4 mr-2" /> Add your first bed
                    </Button>
                </div>
            ) : (
                <div className="grid gap-6">
                    {beds.map((b) => (
                        <div key={b.id} className="journal-card p-6" data-testid={`bed-card-${b.id}`}>
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <div className="overline text-[var(--vm-sage)] text-xs mb-1">{b.type}</div>
                                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">{b.name}</h3>
                                    <div className="flex items-center gap-3 mt-2 text-sm text-[var(--vm-text-secondary)]">
                                        <span>{(b.length_cm / 100).toFixed(1)} × {(b.width_cm / 100).toFixed(1)} m</span>
                                        <span className="flex items-center gap-1"><SunIcon value={b.sun_exposure} /> {b.sun_exposure}</span>
                                        <span className="text-[var(--vm-text-muted)]">{layouts[b.id]?.crop_count ?? 0} crops</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button size="icon" variant="ghost" onClick={() => startEdit(b)} data-testid={`edit-bed-${b.id}`}>
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => remove(b.id)} data-testid={`delete-bed-${b.id}`}>
                                        <Trash2 className="w-4 h-4 text-[var(--vm-secondary)]" />
                                    </Button>
                                </div>
                            </div>
                            {b.soil_notes && (
                                <div className="text-sm text-[var(--vm-text-secondary)] mb-4 italic">{b.soil_notes}</div>
                            )}
                            <Tabs defaultValue="layout" className="w-full">
                                <TabsList className="bg-[var(--vm-input)]" data-testid={`bed-tabs-${b.id}`}>
                                    <TabsTrigger value="layout" data-testid={`tab-layout-${b.id}`}>Layout</TabsTrigger>
                                    <TabsTrigger value="designer" data-testid={`tab-designer-${b.id}`}>Designer</TabsTrigger>
                                </TabsList>
                                <TabsContent value="layout" className="mt-4">
                                    <div className="bed-ascii" data-testid={`bed-ascii-${b.id}`}>
                                        {layouts[b.id]?.ascii || "(loading…)"}
                                    </div>
                                </TabsContent>
                                <TabsContent value="designer" className="mt-4">
                                    <BedGridEditor
                                        bedId={b.id}
                                        onChange={async () => {
                                            // Refetch ASCII so the Layout tab stays in sync.
                                            try {
                                                const layout = await getBedLayout(b.id);
                                                setLayouts(prev => ({ ...prev, [b.id]: layout }));
                                            } catch (e) {
                                                // Layout refresh is a nice-to-have
                                                // — the Designer tab is already
                                                // authoritative. Log in dev only.
                                                if (process.env.NODE_ENV !== "production") {
                                                    console.warn("getBedLayout refresh failed:", e?.message || e);
                                                }
                                            }
                                        }}
                                    />
                                </TabsContent>
                            </Tabs>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
